const ORIGIN = `${process.env.REACT_APP_BACKEND_URL}`;
const BASE = `${ORIGIN}/api`;

let authToken = null;
export function setAuthToken(token) {
  authToken = token;
}
export function getAuthToken() {
  return authToken;
}

/** Anonymous random id per install (no PII) — sent as X-Install-Id for aggregate analytics. */
let installId = null;
let installReady = () => {};
const installPromise = new Promise((r) => (installReady = r));
export function setInstallId(id) {
  installId = id;
  installReady();
}

/** Uploaded files are served at relative /api/files/... — make them absolute everywhere. */
export function assetUrl(u) {
  if (!u) return "";
  return u.startsWith("/api/") ? ORIGIN + u : u;
}
function fixUrls(v) {
  if (typeof v === "string") return v.startsWith("/api/files/") ? ORIGIN + v : v;
  if (Array.isArray(v)) return v.map(fixUrls);
  if (v && typeof v === "object") {
    for (const k of Object.keys(v)) v[k] = fixUrls(v[k]);
  }
  return v;
}
/** Store relative path again before saving to the backend. */
export function relUrl(u) {
  if (!u) return "";
  return u.startsWith(ORIGIN + "/api/files/") ? u.slice(ORIGIN.length) : u;
}

let analyticsOn = true;
export function setAnalyticsEnabled(on) {
  analyticsOn = on;
}

export function track(name, props = {}) {
  Promise.race([installPromise, new Promise((r) => setTimeout(r, 4000))])
    .then(() => (analyticsOn ? api("/events", { method: "POST", body: { name, props } }) : null))
    .catch(() => {});
}

function formatDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return String(detail);
}

export class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** Multipart upload (admin). `file` is a browser File. */
export async function uploadFile(file, kind, path = "/admin/upload") {
  const fd = new FormData();
  fd.append("file", file, file.name);
  fd.append("kind", kind);
  let res;
  try {
    res = await fetch(BASE + path, { method: "POST", headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}, body: fd });
  } catch {
    throw new ApiError("Can't reach BREEZ right now. Check your connection and try again.", 0, "network");
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(formatDetail(data?.detail) ?? `Upload failed (${res.status})`, res.status);
  return fixUrls(data);
}

/** Every call gets a timeout (AI calls pass a longer one). */
export async function api(path, opts = {}) {
  if (!installId) await Promise.race([installPromise, new Promise((r) => setTimeout(r, 1500))]);
  let res;
  let text;
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, opts.timeoutMs ?? 30000);
  if (opts.signal) opts.signal.addEventListener("abort", () => ctrl.abort());
  try {
    res = await fetch(BASE + path, {
      method: opts.method ?? "GET",
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(installId && analyticsOn ? { "X-Install-Id": installId } : {}),
        ...(analyticsOn ? {} : { "X-Analytics": "off" }),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: ctrl.signal,
    });
    text = await res.text();
  } catch {
    if (timedOut) throw new ApiError("This is taking too long. Check your connection and try again.", 0, "timeout");
    if (opts.signal?.aborted) throw new ApiError("Cancelled.", 0, "cancelled");
    throw new ApiError("Can't reach BREEZ right now. Check your connection and try again.", 0, "network");
  } finally {
    clearTimeout(timer);
  }
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) throw new ApiError(formatDetail(data?.detail) ?? `Request failed (${res.status})`, res.status, data?.detail?.code);
  return fixUrls(data);
}

export function openExternal(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

export function downloadDataUrl(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
